import { useState } from 'react';
import {
  Badge,
  Button,
  CombatStatsBlock,
  Countdown,
  EmptyState,
  FleetIcon,
  FleetPicker,
  Panel,
  RadarIcon,
  Stat,
  StatGrid,
  ViewHeader,
  formatAmount,
  formatDurationShort,
  formatPercent,
} from '@spacegame/ui';
import {
  CAMP_TRAVEL_MS,
  COMBAT_CAPABLE,
  UNITS,
  fleetCargo,
  estimateFuelCost,
  fleetTravelTime,
  computeModifiers,
  scoutSuccessChance,
  type ShipKey,
  type UnitKey,
} from '@spacegame/engine';
import { useGame, useGameActions } from '../game/GameProvider';
import { fleetCapacityStatus } from '../game/selectors';

export function CampsView() {
  const { game, clock } = useGame();
  const actions = useGameActions();
  const [fleet, setFleet] = useState<Partial<Record<ShipKey, number>>>({});
  const [scoutProbes, setScoutProbes] = useState(1);
  const now = clock();

  const combatShips = COMBAT_CAPABLE.filter(
    (key) => game.planets[0]!.ships[key as ShipKey] > 0,
  ) as ShipKey[];

  const fleetSize = Object.values(fleet).reduce((a, b) => a + (b ?? 0), 0);
  const fleetRows = combatShips.map((key) => ({ id: key, label: UNITS[key].label, available: game.planets[0]!.ships[key], value: fleet[key] ?? 0 }));
  const fleetFuel = estimateFuelCost(game, fleet);
  const fleetTime = fleetSize > 0 ? fleetTravelTime(CAMP_TRAVEL_MS, fleet, computeModifiers(game, now).flightSpeed) : 0;
  const expedition = game.fleetMissions.find((mission) => mission.kind === 'campAttack');
  const fleetCapacity = fleetCapacityStatus(game);

  return (
    <>
      <ViewHeader
        eyebrow="Heimatsystem · NPC-Ziele"
        title="Piratenlager"
        description="Späh Lager mit Aero-Sonden aus, dann überfalle sie. Die Beute ist durch die Ladekapazität der überlebenden Schiffe begrenzt."
      />

      <StatGrid columns={4}>
        <Stat label="Aero-Sonden" value={String(game.planets[0]!.ships.probe)} accent="aether" hint="Werden beim Spähen verbraucht" />
        <Stat label="Kampfschiffe" value={String(combatShips.reduce((a, k) => a + game.planets[0]!.ships[k], 0))} accent="silicate" />
        <Stat
          label="Ladekapazität"
          value={formatAmount(fleetCargo(game, fleet as Partial<Record<UnitKey, number>>))}
          accent="credit"
          hint="Exakte Auswahl im Angriffsmodul"
        />
        <Stat
          label="Flotten unterwegs"
          value={`${fleetCapacity.active}/${fleetCapacity.capacity}`}
          accent={fleetCapacity.full ? 'danger' : 'silicate'}
          hint={fleetCapacity.capacity === 0 ? 'Benötigt einen Schiffshangar' : 'Schiffshangar-Kapazität'}
        />
      </StatGrid>

      {expedition ? (
        <Panel title="Flotte im Einsatz" eyebrow={expedition.campName} tone="accent">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-content-secondary">
              {expedition.resolved
                ? expedition.won
                  ? 'Lager überrannt — die Flotte kehrt mit Beute zurück.'
                  : 'Angriff gescheitert — die Reste treten den Rückflug an.'
                : 'Anflug läuft. Die Schlacht wird bei Ankunft ausgetragen.'}
            </div>
            <div className="text-xs">
              {expedition.resolved ? 'Rückkehr in ' : 'Ankunft in '}
              <Countdown
                target={expedition.resolved ? expedition.returnsAt : expedition.arrivesAt}
                format="short"
                className="font-semibold"
              />
            </div>
          </div>
        </Panel>
      ) : null}

      <Panel
        title="Angriffsstärke"
        eyebrow="Anteil der Flotte pro Angriff"
        actions={<span className="text-label text-content-muted">Exakte Schiffsauswahl</span>}
      >
        {combatShips.length === 0 ? (
          <p className="text-xs text-content-muted">
            Keine Kampfschiffe im Hangar. Baue Korvetten oder Kreuzer in der Werft.
          </p>
        ) : (
          <FleetPicker rows={fleetRows} onChange={(id, value) => setFleet((current) => ({ ...current, [id]: value }))} totalShips={fleetSize} cargo={fleetCargo(game, fleet)} fuel={fleetFuel} fuelAvailable={game.resources.isotope} travelTimeMs={fleetTime} formatDuration={(ms) => formatDurationShort(ms / 1000)} />
        )}
      </Panel>

      {game.camps.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<RadarIcon size={26} />}
            title="Keine Lager erfasst"
            description="Die Sensoren durchsuchen das Heimatsystem. Neue Lager erscheinen automatisch."
          />
        </Panel>
      ) : (
        <div className="grid gap-3 lg:grid-cols-3">
          {game.camps.map((camp) => {
            const chance = scoutSuccessChance(game, camp, scoutProbes);
            const garrisonSize = Object.values(camp.garrison).reduce((a, b) => a + (b ?? 0), 0);
            const scoutingMission = game.fleetMissions.find(
              (mission): mission is Extract<typeof mission, { kind: 'scoutCamp' }> =>
                mission.kind === 'scoutCamp' && mission.campId === camp.id,
            );
            return (
              <Panel
                key={camp.id}
                title={camp.name}
                eyebrow={`Stufe ${camp.tier + 1} · Auflösung in`}
                tone={camp.tier === 2 ? 'danger' : 'default'}
                actions={<Countdown target={camp.expiresAt} format="short" />}
              >
                <div className="space-y-3">
                  {camp.scouted ? (
                    <>
                      <div>
                        <div className="mb-1 font-display text-label uppercase text-content-muted">
                          Garnison ({garrisonSize} Einheiten)
                        </div>
                        <ul className="space-y-0.5">
                          {(Object.entries(camp.garrison) as [UnitKey, number][]).map(([key, n]) => (
                            <li key={key} className="flex justify-between text-xs">
                              <span className="text-content-secondary">{UNITS[key].label}</span>
                              <span data-sg-numeric className="text-content">{n}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <div className="mb-1 font-display text-label uppercase text-content-muted">
                          Beute
                        </div>
                        <CombatStatsBlock
                          dense
                          stats={{
                            hull: camp.loot.titan,
                            shield: camp.loot.silicate,
                            damage: camp.loot.isotope,
                          }}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs leading-relaxed text-content-muted">
                        Unbekannte Garnison. Sende Aero-Sonden — bei Entdeckung gehen sie verloren.
                      </p>
                      <Badge accent={chance > 0.6 ? 'success' : 'warning'} size="sm">
                        Erfolgschance ca. {formatPercent(chance)}
                      </Badge>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      fullWidth
                      icon={<RadarIcon size={13} />}
                      disabled={camp.scouted || Boolean(scoutingMission) || game.planets[0]!.ships.probe < 1 || fleetCapacity.full}
                      onClick={() => actions.scoutCamp(camp.id, scoutProbes)}
                    >
                      {camp.scouted ? 'Ausgespäht' : scoutingMission ? (
                        <>Sonden unterwegs <Countdown target={scoutingMission.arrivesAt} format="short" /></>
                      ) : 'Spähen'}
                    </Button>
                    {!camp.scouted && !scoutingMission ? (
                      <input className="h-7 w-14 rounded-control border border-edge bg-surface-3 text-center text-xs" type="number" min={1} max={game.planets[0]!.ships.probe} value={scoutProbes} onChange={(event) => setScoutProbes(Math.min(game.planets[0]!.ships.probe, Math.max(1, Number(event.target.value) || 1)))} aria-label="Anzahl Aero-Sonden" />
                    ) : null}
                    <Button
                      variant="danger"
                      size="sm"
                      fullWidth
                      icon={<FleetIcon size={13} />}
                      disabled={fleetSize === 0 || fleetCapacity.full}
                      onClick={() => { void actions.attackCamp(camp.id, fleet); setFleet({}); }}
                      title={
                        fleetCapacity.full
                          ? `Schiffshangar-Kapazität erreicht (${fleetCapacity.active}/${fleetCapacity.capacity} Flotten unterwegs)`
                          : `Flugzeit ${formatDurationShort(CAMP_TRAVEL_MS / 1000)} pro Strecke`
                      }
                    >
                      Angreifen
                    </Button>
                  </div>
                </div>
              </Panel>
            );
          })}
        </div>
      )}
      <p className="text-label text-content-muted">
        Stand: {new Date(now).toLocaleTimeString('de-DE')} · Lager rotieren alle 6 Stunden.
      </p>
    </>
  );
}
